import { useQuery } from '@tanstack/react-query';
import { agentApi } from '../lib/agent.js';
import { AgentVoiceDialog } from './AgentVoiceDialog.js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../lib/session.js';
import { VoiceJobDialog } from './VoiceJobDialog.js';
import { cn } from '../lib/cn.js';

/**
 * VOICE-001 — the way into the live voice screen. Renders nothing unless the
 * session says voice is on.
 *
 * A small glowing orb rather than a microphone: it is the live screen's orb in
 * miniature, so what opens is what was pressed. It also tells it apart from
 * the dictation microphone beside it in the quote editor, which types into one
 * field — this one writes the whole quote. The drawing is CSS only (see
 * `.voice-orb` in design.css); the 36px box is the hit area, the orb inside it
 * is 28px.
 */
export function VoiceButton({
  className,
  agent = false,
  ...dialog
}: Omit<Parameters<typeof VoiceJobDialog>[0], 'onClose'> & { className?: string; agent?: boolean }) {
  const { t } = useTranslation();
  const session = useAuthenticated();
  const [open, setOpen] = useState(false);
  const capabilities = useQuery({ queryKey: ['agent', 'capabilities', session.user.id, session.business.id], queryFn: agentApi.capabilities, enabled: agent, retry: false, staleTime: 60000 });
  useEffect(() => { if (!open) return; document.documentElement.setAttribute('data-worki-voice-open','true'); return () => document.documentElement.removeAttribute('data-worki-voice-open'); }, [open]);
  const useAgent = agent && capabilities.data?.enabled && capabilities.data.version === '2026-10-agent-1';
  if (!session.features.voice) return null;
  return (
    <>
      <button
        type="button"
        aria-label={t('voice.record')}
        title={t('voice.record')}
        onClick={() => { document.documentElement.setAttribute('data-worki-voice-open','true'); window.dispatchEvent(new Event('worki:voice-open')); setOpen(true); }}
        className={cn('voice-orb-button', className)}
      >
        <span className="voice-orb" aria-hidden="true">
          <span className="voice-orb__drift" />
        </span>
      </button>
      {open && useAgent ? <AgentVoiceDialog onClose={() => setOpen(false)} /> : open && (
        <VoiceJobDialog
          {...dialog}
          onReady={(job) => {
            setOpen(false);
            dialog.onReady(job);
          }}
          onUseAsNotes={
            dialog.onUseAsNotes &&
            ((text) => {
              setOpen(false);
              dialog.onUseAsNotes!(text);
            })
          }
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
